import { $t } from "@/lib/i18nHelpers";
import usStates from "@/payments/components/BillingDetailsForm/us-states";
import { FormFields, ListFields } from "@/types";
import { TaxCountry, TinType } from "../../types";

export const canadianProvinces: ListFields = [
  { text: $t("Alberta"), value: "AB" },
  { text: $t("British Columbia"), value: "BC" },
  { text: $t("Manitoba"), value: "MB" },
  { text: $t("New Brunswick"), value: "NB" },
  { text: $t("Newfoundland and Labrador"), value: "NL" },
  { text: $t("Northwest Territories"), value: "NT" },
  { text: $t("Nova Scotia"), value: "NS" },
  { text: $t("Nunavut"), value: "NU" },
  { text: $t("Ontario"), value: "ON" },
  { text: $t("Prince Edward Island"), value: "PE" },
  { text: $t("Quebec"), value: "QC" },
  { text: $t("Saskatchewan"), value: "SK" },
  { text: $t("Yukon"), value: "YT" },
];

export const usStateOptions: ListFields = usStates.map(({ stusab, state_name }) => ({
  text: state_name,
  value: stusab,
}));

export const taxCountryOptions: ListFields = [
  { text: $t("USA"), value: TaxCountry.USA },
  { text: $t("Canada"), value: TaxCountry.CANADA },
];

const zipPatterns: Record<TaxCountry, RegExp> = {
  [TaxCountry.USA]: /^\d{5}(-\d{4})?$/,
  [TaxCountry.CANADA]: /^[A-Za-z]\d[A-Za-z][ -]?\d[A-Za-z]\d$/,
};

export function taxpayerInfoFields(country: TaxCountry): FormFields {
  return [
    {
      name: "legalFirstName",
      label: $t("Legal first name"),
      rules: ["required"],
      validationMode: "aggressive",
    },
    {
      name: "legalMiddleName",
      label: $t("Legal middle name (optional)"),
    },
    {
      name: "legalLastName",
      label: $t("Legal last name"),
      rules: ["required"],
      validationMode: "aggressive",
    },
    {
      name: "businessName",
      label: $t("Business name, if different from above"),
      hint: $t("Enter your business/DBA name"),
    },
    {
      name: "addressSubtitle",
      type: "subtitle",
      label: $t("Business or permanent address"),
    },
    {
      name: "addressLine1",
      label: $t("Address line 1"),
      rules: ["required"],
      validationMode: "aggressive",
    },
    {
      name: "addressLine2",
      label: $t("Address line 2 (optional)"),
    },
    {
      name: "zipCode",
      label: $t("Zip code / postal code"),
      rules: { required: true, regex: zipPatterns[country] },
      rulesMessages: { regex: $t("Enter a valid zip / postal code") },
      validationMode: "aggressive",
    },
    {
      name: "city",
      label: $t("City"),
      rules: ["required"],
      validationMode: "aggressive",
    },
    {
      name: "state",
      type: "select",
      label: $t("State / province"),
      options: country === TaxCountry.CANADA ? canadianProvinces : usStateOptions,
      rules: ["required"],
    },
    {
      name: "country",
      type: "select",
      label: $t("Country"),
      options: taxCountryOptions,
      rules: ["required"],
    },
  ];
}

export const tinOptions: ListFields = [
  { text: $t("SSN / ITIN"), value: TinType.SSN_ITIN },
  { text: $t("EIN"), value: TinType.EIN },
  { text: $t("SIN"), value: TinType.SIN },
];

export const tinPatterns: Record<TinType, RegExp> = {
  [TinType.SSN_ITIN]: /^\d{3}-?\d{2}-?\d{4}$/,
  [TinType.EIN]: /^\d{2}-?\d{7}$/,
  [TinType.SIN]: /^\d{3}[- ]?\d{3}[- ]?\d{3}$/,
};
